import { Dialog } from "@headlessui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Dispatch, SetStateAction, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormBox, ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { TextField } from "../../components/fields/Fields";
import FormController from "../../components/fields/FormController";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { useInput } from "../../hooks/useInput";
import { Groups, groupSchema } from "../../model/Groups";
import { UserData } from "../../model/Users";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { Spiner } from "../../components/spinner/Spiner";

export type GroupAddModalProps = {
  group?: Groups;
  isUpdate: boolean;
  groupUserList?: UserData[];
  cols: GridCol[];
  rows: GridRow[];
  onClickSubmit: (data: Groups) => Promise<void>;
  setIsDeleteGroup: Dispatch<SetStateAction<"deleteFalse" | "deleteTrue">>;
  firstPageFlg?: boolean;
  setFirstPageFlg?: Function;
  pagingFunc?: (ver: number) => void;
  totalCount?: number;
  isLode?: boolean;
}

export const GroupAddModal = ({
  open,
  toggle,
  closeOnClickOutside,
  group,
  isUpdate,
  groupUserList,
  cols,
  rows,
  onClickSubmit,
  setIsDeleteGroup,
  firstPageFlg = false,
  setFirstPageFlg,
  pagingFunc,
  totalCount,
  isLode = false,
}: ModalProp & GroupAddModalProps) => {

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<Groups>({
      resolver: yupResolver(groupSchema),
      defaultValues: group,
    });

  const deleteFlgItem: RadioButtonItem[] = [
    {
      id: "deleteFalse",
      title: "有効",
    },
    {
      id: "deleteTrue",
      title: "無効",
    },
  ];
  const deleteFlg = useInput("deleteFalse");

  useEffect(() => {
    if(open){
      setValue("id", group?.id ?? "");
      setValue("name", group?.name ?? "");
      setValue("nameKana", group?.nameKana ?? "");
      setValue("explanation", group?.explanation ?? "");
      setValue("groupColor", group?.groupColor ?? "");

      deleteFlg.setValue(group?.deleteFlg ? "deleteTrue" : "deleteFalse");
      // 重複チェック初期化
      sessionStorage.removeItem("dupCheck")
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[ group?.deleteFlg, group?.explanation, group?.groupColor, group?.name,
    group?.nameKana, isUpdate, setValue, open ]);

  useEffect(() => {
    setValue("deleteFlg", (deleteFlg.value === "deleteTrue"));
    setIsDeleteGroup(deleteFlg.value);
  },[deleteFlg.value, setIsDeleteGroup, setValue]);

  useEffect(() => {
    if(!open){
      reset();
      deleteFlg.setValue(group?.deleteFlg ? "deleteTrue" : "deleteFalse");
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[group?.deleteFlg, open, reset]);

  // グループ名称変更時
  useEffect(() => {
    // dupCheckをリセット(false)
    sessionStorage.setItem("dupCheck", "false");
    // 入力値更新
    setValue("name",watch("name"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[watch("name")]);

  return (
    <Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
    >
      <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white  text-left shadow-xl transition-all sm:w-full max-w-screen-lg ">
        <div className="px-2 pb-4 sm:p-4">
          <div className="text-center sm:mt-3">
            <Dialog.Title
              as="h3"
              className="text-left text-lg font-medium leading-6 text-gray-900"
            >
              グループ {isUpdate ? "編集" : "新規作成"}
            </Dialog.Title>
            <div className="overflow-y-auto">
              <div className="px-2 text-left pb-8 max-h-[calc(100vh_-_15rem)] ">
                <FormBox>
                  <FormBox gridcol="sm:grid-cols-1 sm:pt-[0px]">
                    <Controller
                      name={"name"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type={"text"}
                          label={"名称"}
                          required
                          error={fieldState.error || sessionStorage.getItem("dupCheck") === "true"}
                          helperText={fieldState.error?.message || (sessionStorage.getItem("dupCheck") === "true" ? "このグループ名は既に登録されています。" : "")}
                        />
                      )}
                    />
                    <FormController
                      control={control}
                      name={"nameKana"}
                      label={"フリガナ"}
                      type={"text"}
                      required
                    />
                    <RadioButton
                      vertical
                      label={"有効/無効"}
                      children={undefined}
                      value={deleteFlg}
                      name={"deleteFlg"}
                      radioButtonItem={deleteFlgItem}
                    />
                  </FormBox>
                  <Controller
                    name="explanation"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        type="textarea"
                        label="説明"
                        className="h-48"
                      />
                    )}
                  />
                </FormBox>
                <div className={"pt-9"}>
                 {isLode
                  ? (<Spiner/>)
                    : groupUserList !== undefined && groupUserList?.length > 0
                  ? (
                    <TableList 
                      header 
                      gridCol={cols} 
                      gridRow={rows}
                      firstPageFlg={firstPageFlg}
                      setFirstPageFlg={setFirstPageFlg}
                      pagingFunc={pagingFunc}
                      gridRowLength={totalCount}
                    />
                  )
                    : (<></>)
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
        <ModalFoterBox>
          <Button
            className="mx-2 mt-1 w-[132px]"
            outline
            onClick={toggle}
            children={"キャンセル"}
          />
          <Button
            className="mt-1 w-[132px]"
            onClick={handleSubmit(onClickSubmit)}
            children={"登録"}
          />
        </ModalFoterBox>
      </Dialog.Panel>
    </Modal>
  )
}